import React from 'react';
import { IProduct, IBuckleStrapSpecification } from '../../interfaces';
import Slider from 'react-slick';
import { SliderNext, SliderPrev } from '../Arrows';
import { getProductSpecificationsByLang } from '../../utils/product';
import { getProductDisplayName } from './../../utils/product';
import { useTranslation } from '../../i18n';
import { getWatchDialImagesUrl } from '../../utils/image';
import { performSmoothScroll } from './../../utils/window';
const StrapSelector = ({
  straps,
  selectedStrap,
  onSelectStrap,
  currentDial
}: {
  straps: IProduct[];
  onSelectStrap: (strap: IProduct) => void;
  currentDial: IProduct;
  selectedStrap: IProduct;
}) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language || 'EN';
  const hasVariations = straps.length > 1;

  const [showInfo, setShowInfo] = React.useState(true);
  const pagingRefs = straps.map(() => React.useRef<HTMLImageElement>());

  const currentIndex =
    straps.length === 1
      ? 0
      : straps.findIndex((strap) => strap.id === selectedStrap.id);

  React.useEffect(() => {
    setTimeout(() => setShowInfo(true), 500);
  }, []);

  React.useEffect(() => {
    pagingRefs?.[currentIndex]?.current?.click();
  }, [currentIndex]);

  const specs = getProductSpecificationsByLang(
    selectedStrap,
    language
  ) as IBuckleStrapSpecification;

  const sliderSettings = {
    nextArrow: <SliderNext />,
    prevArrow: <SliderPrev />,
    focusOnSelect: true,
    centerMode: true,
    variableWidth: true,
    arrows: hasVariations,
    infinite: false,
    initialSlide: currentIndex,
    afterChange: (index) => {
      setShowInfo(true);
      if (currentIndex !== index) {
        onSelectStrap(straps[index]);
      }
    },
    beforeChange: () => {
      setShowInfo(false);
    },
    onSwipe: () => {
      setShowInfo(false);
    },
    dots: true,
    customPaging: (i) => (
      <img ref={pagingRefs[i]} src={straps[i].imageUrls[0]} />
    ),
    dotsClass: 'slick-dots accessories-thumb',
    responsive: [
      {
        breakPoint: 992,
        settings: {
          arrows: hasVariations
        }
      }
    ]
  };

  const noSlider = straps.length === 1;
  return (
    <div className={`StrapSelector ${noSlider && 'no-slider'}`}>
      <div className="StrapSelector-inner">
        <img
          src={getWatchDialImagesUrl(currentDial.previewImageUrl)}
          alt="Dial"
          className="dial"
        />
        <div className={`info strap-info-line ${showInfo}`} />
        <div className={`info strap-info ${showInfo}`}>
          {specs?.material && <span>{specs.material}</span>}
          {specs?.color &&
            specs?.color?.toLowerCase() !== specs?.material?.toLowerCase() && (
              <span>{specs.color}</span>
            )}
          {specs?.size && <span>{specs.size}</span>}
          <span className="modelCode">{selectedStrap?.modelCode}</span>

          <button
            onClick={() => performSmoothScroll('#buckleSection')}
            className={`btn btn-sm btn-light mt-2 buckle-select ${showInfo}`}
          >
            {t('select_buckle')}
          </button>
        </div>

        <div className="info watch-info">
          <span>{getProductDisplayName(currentDial, language)}</span>
          <span>{currentDial.modelCode}</span>
        </div>

        {noSlider ? (
          <div className="strap-thumb single-thumb">
            <img
              src={straps[0].imageUrls[0]}
              className="strap-top"
              alt="Strap Top"
            />
            <img
              src={straps[0].imageUrls[1]}
              className="strap-bottom"
              alt="Strap Bottom"
            />
          </div>
        ) : (
          <div className="slider-container">
            <Slider {...sliderSettings}>
              {straps.map((strap, index) => (
                <div key={strap.id + index} className="strap-thumb">
                  <img
                    src={strap.imageUrls[0]}
                    className="strap-top"
                    alt="Strap Top"
                  />
                  <img
                    src={strap.imageUrls[1]}
                    className="strap-bottom"
                    alt="Strap Bottom"
                  />
                </div>
              ))}
            </Slider>
          </div>
        )}
      </div>
      <style jsx global>{`
        .accessories-thumb {
          bottom: 10px;
        }
        .accessories-thumb li {
          width: 40px;
          height: 40px;
          background: #efefef;
          border-radius: 100%;
          overflow: hidden;
          opacity: 0.5;
          margin: 2px;
          border: 2px solid #efefef;
        }
        .accessories-thumb img {
          width: 100%;
        }
        .accessories-thumb li.slick-active {
          opacity: 1;
          border-color: #333;
          background: #fff;
        }
        @media (max-width: 768px) {
          .accessories-thumb li {
            width: 25px;
            height: 25px;
          }
        }
      `}</style>
      <style jsx>{`
        .StrapSelector-inner {
          overflow: hidden;
          position: relative;
        }
        .single-thumb {
          margin: auto;
          z-index: 1;
          position: relative;
          width: 100%;
        }
        .single-thumb img {
          display: block;
          margin: 0 auto;
        }
        .StrapSelector {
          position: relative;
          margin: auto;
          overflow: hidden;
          z-index: 2;
          display: flex;
          flex-direction: column;
        }
        .StrapSelector.no-slider {
          display: block;
          width: 100%;
        }
        .StrapSelector :global(.slider-arrow) {
          top: auto;
          top: 368px;
          transform: translate(0, -60px);
        }
        .StrapSelector :global(.slick-slider) {
          max-height: calc(100vh - 45px);
        }
        .info {
          position: absolute;
          border-top: 1px solid #ddd;
          transition: all 0.3s ease-in-out;
          opacity: 1;
          top: 368px;
        }
        .info.false {
          opacity: 0;
          transition: none;
        }
        .info span {
          display: block;
          font-size: 0.7em;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: #666;
        }
        .info span:first-child {
          font-size: 0.7em;
          color: #000;
          font-weight: bold;
        }

        .strap-info,
        .strap-info-line {
          left: 50%;
          margin-top: -184px;
        }
        .strap-info-line {
          width: 170px;
        }
        .strap-info {
          width: 120px;
          text-align: right;
          margin-left: 50px;
          z-index: 10;
          border-top: none;
        }

        .watch-info {
          text-align: left;
          padding-right: 70px;
          right: 50%;
          width: 200px;
        }
        .buckle-select {
          font-size: 0.7em;
          background: #ccc;
        }

        .strap-thumb {
          outline: none;
        }

        .strap-thumb img,
        .single-thumb img,
        .dial {
          width: 350px;
        }
        .slider-container {
          z-index: 2;
          position: relative;
          overflow: hidden;
        }

        .dial {
          position: absolute;
          z-index: 3;
          left: 50%;
          top: 368px;
          margin-left: -175px;
          margin-top: -175px;
          pointer-events: none;
        }
        .buckle-select {
          display: none;
        }

        @media (max-width: 992px) {
          .StrapSelector :global(.slider-arrow) {
            display: none;
          }
        }

        @media (max-height: 880px) and (min-width: 768px) {
          .StrapSelector-inner {
            height: 100vh;
          }
          .StrapSelector :global(.slick-slider) {
            max-height: 100vh;
          }
          .strap-thumb img,
          .single-thumb img,
          .dial {
            width: 300px;
          }
          .StrapSelector :global(.slider-arrow),
          .info,
          .dial {
            top: 315.43px;
          }

          .dial {
            margin-left: -150px;
            margin-top: -150px;
          }
        }

        @media (max-width: 768px) {
          .buckle-select {
            display: inline-block;
          }
          .StrapSelector {
            width: 100vw;
            min-width: 370px;
          }
          .StrapSelector-inner {
            height: 100vh;
          }
          .StrapSelector :global(.slick-slider) {
            max-height: 100vh;
          }
          .StrapSelector :global(.slider-arrow),
          .info,
          .dial {
            top: 283.89px;
          }

          .strap-thumb img,
          .single-thumb img,
          .dial {
            width: 270px;
          }
          .dial {
            margin-left: -136px;
            margin-top: -135px;
          }

          .strap-info,
          .strap-info-line {
            margin-top: -142px;
          }
          .strap-info-line {
            width: 160px;
          }
          .strap-info {
            width: 110px;
          }
          .watch-info {
            width: 170px;
          }
        }
      `}</style>
    </div>
  );
};

export default StrapSelector;
